import { Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-388 — Posting-streak chip (UX Spec §2).
 *
 * One presentational primitive wired into three creator surfaces:
 *   - W8 missions home + W13 active profile → the ACTIVE chip
 *     ("🔥 N day streak", accent fill) when `currentDays >= 1`.
 *   - W19 dormant profile → the BROKEN chip ("Streak broken", muted, no
 *     flame, no number) when `broken`.
 *
 * Meaning is carried by TEXT, never by the flame icon or accent colour
 * alone (a11y, UX Spec §6): the active label reads "N day streak"; the
 * broken label reads "Streak broken". The `Flame` icon is `aria-hidden`,
 * and `aria-label` carries the full, untruncated label so screen readers
 * always read the complete state even when the visible text truncates at
 * narrow widths.
 *
 * Non-interactive (no tap target), consistent with `RankPill`.
 *
 * # Locked testids
 *
 *   - `streak-chip` — active variant.
 *   - `creator-identity-streak-broken` — broken variant (reuses the
 *     OKS-387 W19 contract so existing specs keep passing).
 */
interface StreakChipProps {
  /** Current consecutive-day run. The active chip renders only when >= 1. */
  currentDays: number
  /** W19 broken variant — overrides the active chip. Defaults to false. */
  broken?: boolean
  className?: string
}

export function StreakChip({
  currentDays,
  broken = false,
  className,
}: StreakChipProps) {
  const { t } = useTranslation()

  if (broken) {
    return (
      <Badge
        variant="secondary"
        data-testid="creator-identity-streak-broken"
        className={cn("text-muted-foreground", className)}
      >
        {t("streak.chip.broken")}
      </Badge>
    )
  }

  if (currentDays < 1) {
    return null
  }

  const label = t("streak.chip.active", { count: currentDays })

  return (
    <Badge
      variant="default"
      data-testid="streak-chip"
      aria-label={label}
      className={cn("max-w-full truncate", className)}
    >
      <Flame aria-hidden="true" />
      <span className="truncate">{label}</span>
    </Badge>
  )
}
